import React, { useState } from 'react';
import { apiService } from '../../services/ApiService';
import './ContactPage.css';
import familyBackground from '../../assets/family-background.png';

export const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    tipoContato: 'locacao',
    mensagem: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await apiService.sendContact({
        nome: formData.nome,
        email: formData.email,
        telefone: formData.telefone || undefined,
        tipoContato: formData.tipoContato,
        mensagem: formData.mensagem
      });

      if (response.success) {
        setSubmitStatus('success');
        setFormData({
          nome: '',
          email: '',
          telefone: '',
          tipoContato: 'locacao',
          mensagem: ''
        });
      } else {
        setSubmitStatus('error');
        console.error('Erro ao enviar contato:', response.message);
      }
    } catch (error: any) {
      setSubmitStatus('error');
      console.error('Erro ao enviar contato:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Imagem de fundo */}
      <div 
        className="background-image"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `url(${familyBackground})`,
          backgroundSize: '100% auto',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          zIndex: 1
        }}
      />
      
      {/* Overlay claro para melhor contraste */}
      <div 
        className="background-overlay"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.35))',
          zIndex: 2
        }}
      />

      <div className="contact-container">
        <div className="contact-header">
          <h1>Entre em Contato</h1>
          <div className="contact-divider"></div>
          <p>Estamos aqui para ajudar você a encontrar o imóvel perfeito em Balneário Camboriú</p>
        </div>

        <div className="contact-content">
          {/* Card CTA movido da página Sobre */}
          <div className="contact-cta">
            <h2>Pronto para encontrar seu lar ideal?</h2>
            <p>Entre em contato conosco e descubra como podemos transformar sua busca por imóveis</p>
            <div className="cta-buttons">
              <a href="https://wa.me/5547988569815" className="btn-whatsapp" target="_blank" rel="noopener noreferrer">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  style={{ marginRight: '8px' }}
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M20.52 3.48A11.82 11.82 0 0 0 12.06 0C5.5 0 .22 5.28.22 11.84c0 2.09.54 4.14 1.56 5.95L0 24l6.36-1.65c1.74.95 3.7 1.45 5.7 1.45h.01c6.56 0 11.84-5.28 11.84-11.84 0-3.17-1.24-6.14-3.39-8.48zM12.07 21.5h-.01c-1.75 0-3.46-.47-4.96-1.36l-.36-.21-3.77.98 1.01-3.67-.24-.38a9.77 9.77 0 0 1-1.52-5.11C2.22 6.4 6.6 2.02 12.06 2.02c2.62 0 5.08 1.02 6.93 2.88a9.74 9.74 0 0 1 2.86 6.94c0 5.46-4.38 9.66-9.78 9.66zm5.66-7.28c-.31-.16-1.82-.9-2.1-1-.28-.1-.48-.16-.68.16-.2.31-.78 1-.96 1.2-.18.2-.36.22-.67.06-.31-.16-1.3-.48-2.47-1.52-.91-.81-1.52-1.8-1.7-2.1-.18-.31-.02-.48.14-.64.14-.14.31-.36.46-.54.15-.18.2-.31.31-.52.1-.2.05-.38-.02-.54-.06-.16-.68-1.63-.93-2.23-.24-.58-.48-.5-.68-.5-.18 0-.38-.02-.58-.02-.2 0-.52.08-.79.38-.27.31-1.04 1.02-1.04 2.48 0 1.47 1.06 2.88 1.21 3.08.15.2 2.1 3.2 5.07 4.49.71.31 1.26.5 1.69.64.71.23 1.36.2 1.87.12.57-.08 1.82-.74 2.08-1.45.26-.71.26-1.32.18-1.45-.08-.12-.28-.2-.59-.36z" />
                </svg>
                Falar no WhatsApp
              </a>
            </div>
          </div>

          {/* Formulário de Contato */}
          <div className="contact-form-section">
            <div className="form-header">
              <h3>Envie-nos uma Mensagem</h3>
              <p>Preencha o formulário abaixo e entraremos em contato em breve</p>
            </div>

            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nome">Nome Completo *</label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    required
                    placeholder="Seu nome completo"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">E-mail *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="seu@email.com"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="telefone">Telefone</label>
                  <input
                    type="tel"
                    id="telefone"
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleInputChange}
                    placeholder="(47) 99999-9999"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="tipoContato">Tipo de Contato</label>
                  <select
                    id="tipoContato"
                    name="tipoContato"
                    value={formData.tipoContato}
                    onChange={handleInputChange}
                  >
                    <option value="locacao">Busco imóvel para locação</option>
                    <option value="venda">Busco imóvel para compra</option>
                    <option value="anunciar">Quero anunciar meu imóvel</option>
                    <option value="outros">Outros assuntos</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="mensagem">Mensagem *</label>
                <textarea
                  id="mensagem"
                  name="mensagem"
                  value={formData.mensagem}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  placeholder="Conte-nos sobre suas necessidades, preferências de localização, orçamento ou qualquer outra informação relevante..."
                />
              </div>

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      Enviando...
                    </>
                  ) : (
                    'Enviar Mensagem'
                  )}
                </button>
              </div>

              {submitStatus === 'success' && (
                <div className="success-message">
                   Mensagem enviada com sucesso! Entraremos em contato em breve.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="error-message">
                   Erro ao enviar mensagem. Tente novamente ou entre em contato pelo WhatsApp.
                </div>
              )}
            </form>
          </div>

          {/* Informações de Contato */}
          <div className="contact-info">
            <div className="info-card">
              <div className="info-icon">
                
              </div>
              <h4>Telefone</h4>
              <p>(47) 98856-9815</p>
            </div>

            <div className="info-card">
              <div className="info-icon">
                
              </div>
              <h4>WhatsApp</h4>
              <p>Atendimento rápido</p>
              <a href="https://wa.me/5547988569815" target="_blank" rel="noopener noreferrer">
                Falar no WhatsApp
              </a>
            </div>

            <div className="info-card">
              <div className="info-icon"></div>
              <h4>E-mail</h4>
              <p>Entre em contato</p>
              <a href="mailto:contato@sandimobiliaria.com.br">Enviar e-mail</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
